import { useEffect, useState } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  declaredTokens,
  isPaintable,
  resolvedValue,
  type Token,
} from '@/features/style-guide/tokens'
import { useUiStore, type Theme } from '@/shared/stores/ui-store'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Progress } from '@/shared/ui/progress'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'

const THEMES: readonly Theme[] = ['system', 'light', 'dark']

/**
 * Every design token, and what each one currently looks like.
 *
 * The place to eyeball a token change: alter a value in `index.css`, load this,
 * and see both the number and everything painted with it, in either theme,
 * without hunting for a screen that happens to use it.
 *
 * It replaces Storybook rather than sitting beside it. The 28 stories the Vue
 * app carried documented primitives shadcn already documents upstream, at the
 * cost of a second build target and a compose service; what they did not cover
 * is the one thing this fork actually changed, which is the tokens.
 */
export function StyleGuidePage() {
  const { t } = useTranslation()
  const theme = useUiStore((state) => state.theme)
  const setTheme = useUiStore((state) => state.setTheme)
  const [tokens, setTokens] = useState<Token[] | null>(null)

  useEffect(() => {
    // After paint, because the answer is read from the stylesheets the document
    // has loaded rather than from anything this module imports.
    setTokens(declaredTokens(Array.from(document.styleSheets)))
  }, [])

  const of = (kind: Token['kind']): Token[] =>
    (tokens ?? []).filter((token) => token.kind === kind)
  const palette = of('palette').filter((token) => paintable(token))
  const measures = of('palette').filter((token) => !paintable(token))

  return (
    <div className="mx-auto w-full max-w-5xl space-y-10 p-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{t('styleGuide.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('styleGuide.why')}</p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="style-guide-theme">{t('settings.theme')}</Label>
          <NativeSelect
            id="style-guide-theme"
            value={theme}
            onChange={(event) => {
              setTheme(event.target.value as Theme)
            }}
          >
            {THEMES.map((option) => (
              <option key={option} value={option}>
                {t(`settings.themes.${option}` as 'settings.themes.system')}
              </option>
            ))}
          </NativeSelect>
        </div>
      </header>

      {tokens !== null && tokens.length === 0 && (
        // Not the same as "there are no tokens": the stylesheet is there and
        // this page could not read it. Saying so beats an empty page that reads
        // as a design system with nothing in it.
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>{t('styleGuide.unreadableTitle')}</AlertTitle>
          <AlertDescription>{t('styleGuide.unreadableDescription')}</AlertDescription>
        </Alert>
      )}

      <Section title={t('styleGuide.palette')} note={t('styleGuide.paletteNote')}>
        <Swatches tokens={palette} />
      </Section>

      <Section title={t('styleGuide.sidebarPalette')}>
        <Swatches tokens={of('sidebar')} />
      </Section>

      <Section title={t('styleGuide.typography')}>
        <ul className="space-y-2">
          {of('font').map((token) => (
            <li key={token.name} className="rounded-lg border p-3">
              <TokenName token={token} />
              <p className="mt-2 text-lg" style={{ fontFamily: `var(${token.name})` }}>
                {t('styleGuide.specimen')}
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-1">
          <p className="font-heading text-2xl font-semibold">{t('styleGuide.heading')}</p>
          <p className="text-base">{t('styleGuide.body')}</p>
          <p className="text-muted-foreground text-sm">{t('styleGuide.secondary')}</p>
          <p className="font-mono text-sm">{t('styleGuide.monospaced')}</p>
        </div>
      </Section>

      <Section title={t('styleGuide.radii')} note={t('styleGuide.radiiNote')}>
        <div className="flex flex-wrap gap-4">
          {of('radius').map((token) => (
            <div key={token.name} className="space-y-2">
              <div
                className="bg-accent size-20"
                style={{ borderRadius: `var(${token.name})` }}
              />
              <TokenName token={token} />
            </div>
          ))}
        </div>
      </Section>

      {measures.length > 0 && (
        <Section title={t('styleGuide.other')}>
          <ul className="divide-y rounded-lg border">
            {measures.map((token) => (
              <li key={token.name} className="p-3">
                <TokenName token={token} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Separator />

      <Section title={t('styleGuide.components')} note={t('styleGuide.componentsNote')}>
        <div className="flex flex-wrap items-center gap-2">
          <Button>{t('action.create')}</Button>
          <Button variant="secondary">{t('action.rename')}</Button>
          <Button variant="outline">{t('action.duplicate')}</Button>
          <Button variant="ghost">{t('action.cancel')}</Button>
          <Button variant="destructive">{t('action.delete')}</Button>
          <Button disabled>{t('action.refresh')}</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge>{t('styleGuide.badge')}</Badge>
          <Badge variant="secondary">{t('styleGuide.badge')}</Badge>
          <Badge variant="outline">{t('styleGuide.badge')}</Badge>
          <Badge variant="destructive">{t('styleGuide.badge')}</Badge>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="style-guide-input">{t('styleGuide.field')}</Label>
            <Input id="style-guide-input" placeholder={t('styleGuide.placeholder')} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="style-guide-textarea">{t('styleGuide.longField')}</Label>
            <Textarea
              id="style-guide-textarea"
              placeholder={t('styleGuide.placeholder')}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch id="style-guide-switch" defaultChecked />
            <Label htmlFor="style-guide-switch">{t('styleGuide.switch')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="style-guide-checkbox" defaultChecked />
            <Label htmlFor="style-guide-checkbox">{t('styleGuide.checkbox')}</Label>
          </div>
        </div>

        <div className="space-y-3">
          <Progress value={62} />
          <Skeleton className="h-8 w-2/3" />
        </div>

        <div className="space-y-3">
          <Alert>
            <Info />
            <AlertTitle>{t('styleGuide.alert')}</AlertTitle>
            <AlertDescription>{t('styleGuide.alertBody')}</AlertDescription>
          </Alert>
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>{t('styleGuide.alertBad')}</AlertTitle>
            <AlertDescription>{t('styleGuide.alertBody')}</AlertDescription>
          </Alert>
        </div>
      </Section>
    </div>
  )
}

function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-medium">{title}</h2>
        {note !== undefined && <p className="text-muted-foreground text-sm">{note}</p>}
      </div>
      {children}
    </section>
  )
}

function Swatches({ tokens }: { tokens: Token[] }) {
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {tokens.map((token) => (
        <li key={token.name} className="space-y-2">
          <div
            // Bordered rather than bare: a token that resolves to the same
            // colour as the page behind it is invisible without one, and being
            // invisible is the most likely thing to be wrong with it.
            className="h-16 w-full rounded-lg border"
            style={{ background: `var(${token.name})` }}
          />
          <TokenName token={token} />
        </li>
      ))}
    </ul>
  )
}

/** The name to change, and what it currently comes out as. */
function TokenName({ token }: { token: Token }) {
  const value = resolvedValue(token.name, document.documentElement)
  return (
    <div className="space-y-0.5">
      <p className="font-mono text-xs break-all">{token.name}</p>
      <p className="text-muted-foreground font-mono text-xs break-all">{value}</p>
    </div>
  )
}

function paintable(token: Token): boolean {
  return isPaintable(resolvedValue(token.name, document.documentElement))
}
